import { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material'
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  WhatsApp as WhatsAppIcon,
  Send as SendIcon,
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import LegalHero from './legal/LegalHero'

const MotionBox = motion(Box)
const MotionPaper = motion(Paper)

const contactInfo = [
  {
    icon: <PhoneIcon />,
    title: 'Call Us',
    details: ['+91 7978190633'],
    action: {
      label: 'Call Now',
      href: 'tel:+917978190633',
    }
  },
  {
    icon: <WhatsAppIcon />,
    title: 'WhatsApp',
    details: ['+91 7978190633'],
    action: {
      label: 'Message Us',
      href: 'https://wa.me/917978190633',
    }
  },
  {
    icon: <EmailIcon />,
    title: 'Email Us',
    details: ['hotelbisworupainn@gmail.com'],
    action: {
      label: 'Send Email',
      href: 'mailto:hotelbisworupainn@gmail.com',
    }
  },
  {
    icon: <TimeIcon />,
    title: 'Hotel Timings',
    details: ['Check-in: 2:00 PM', 'Check-out: 12:00 PM', '24/7 Front Desk'],
  },
]

const Contact = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Handle form submission
    console.log(formData)
  }

  return (
    <Box>
      <LegalHero 
        title="Get in Touch"
        subtitle="We're here to make your stay at Bisworupa Inn memorable. Reach out to us for any assistance or inquiries."
      />

      {/* Main Content */}
      <Box 
        sx={{ 
          position: 'relative',
          bgcolor: 'background.default',
          pb: { xs: 6, sm: 8, md: 12 },
        }}
      >
        {/* Floating Contact Card */}
        <Container 
          maxWidth="xl" 
          sx={{ 
            transform: { xs: 'translateY(-40px)', sm: 'translateY(-60px)', md: 'translateY(-80px)' },
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <MotionPaper
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            elevation={3}
            sx={{
              borderRadius: { xs: 2, sm: 3, md: 4 },
              overflow: 'hidden',
              bgcolor: 'background.paper',
              boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
            }}
          >
            <Grid container>
              {/* Quick Contact Info */}
              <Grid item xs={12} md={4}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  p: { xs: 3, sm: 4, md: 6 },
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: 24,
                    background: `linear-gradient(to left, ${alpha(theme.palette.primary.main, 0)}, ${theme.palette.primary.main})`,
                    transform: 'translateX(24px)',
                    display: { xs: 'none', md: 'block' },
                  }
                }}
              >
                <Typography 
                  variant="h4" 
                  gutterBottom 
                  sx={{ 
                    fontFamily: '"Playfair Display", serif',
                    fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                    mb: { xs: 2, md: 3 },
                  }}
                >
                  Contact Info
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mb: { xs: 3, md: 4 }, 
                    opacity: 0.9,
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                  }}
                >
                  Feel free to reach out to us through any of these channels
                </Typography>
                <Stack spacing={{ xs: 3, md: 4 }}>
                  {contactInfo.map((info, index) => (
                    <MotionBox
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <Stack 
                        direction="row" 
                        spacing={2} 
                        alignItems="flex-start"
                        sx={{
                          transition: 'transform 0.2s ease',
                          '&:hover': {
                            transform: 'translateX(8px)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            p: { xs: 1.25, md: 1.5 },
                            borderRadius: 2,
                            bgcolor: alpha('#fff', 0.1),
                            display: 'flex',
                            '& svg': {
                              fontSize: { xs: '1.25rem', sm: '1.5rem' },
                            },
                          }}
                        >
                          {info.icon}
                        </Box>
                        <Box>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontSize: { xs: '1.1rem', sm: '1.25rem' },
                              mb: 1,
                            }}
                          >
                            {info.title}
                          </Typography>
                          {info.details.map((detail, idx) => (
                            <Typography 
                              key={idx} 
                              variant="body2" 
                              sx={{ 
                                opacity: 0.9,
                                fontSize: { xs: '0.9rem', sm: '0.95rem' },
                                mb: 0.5,
                              }}
                            >
                              {detail}
                            </Typography>
                          ))}
                          {info.action && (
                            <Button
                              variant="text"
                              href={info.action.href}
                              target={info.action.href.startsWith('http') ? '_blank' : undefined}
                              sx={{
                                mt: { xs: 1, md: 1.5 },
                                color: 'white',
                                fontSize: { xs: '0.9rem', sm: '0.95rem' },
                                p: { xs: 0.5, sm: 1 },
                                '&:hover': {
                                  bgcolor: alpha('#fff', 0.1),
                                  transform: 'translateX(4px)',
                                },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              {info.action.label} →
                            </Button>
                          )}
                        </Box>
                      </Stack>
                    </MotionBox>
                  ))}
                </Stack>
              </Grid>

              {/* Contact Form */}
              <Grid item xs={12} md={8}>
                <Box 
                  component="form" 
                  onSubmit={handleSubmit}
                  sx={{ 
                    p: { xs: 3, sm: 4, md: 6 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontFamily: '"Playfair Display", serif',
                      fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                      mb: { xs: 1, md: 2 },
                    }}
                  >
                    Send us a Message
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: { xs: 3, md: 4 }, 
                      color: 'text.secondary',
                      fontSize: { xs: '0.95rem', sm: '1rem' },
                      lineHeight: 1.6,
                    }}
                  >
                    We'll get back to you as soon as possible
                  </Typography>

                  <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Your Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Your Message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        multiline
                        rows={4}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        endIcon={<SendIcon />}
                        sx={{
                          mt: { xs: 1, sm: 2 },
                          py: { xs: 1.5, sm: 2 },
                          px: { xs: 4, sm: 6 },
                          borderRadius: 2,
                          fontSize: { xs: '0.95rem', sm: '1rem' },
                          fontWeight: 600,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          '&:hover': {
                            boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                            transform: 'translateY(-2px)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        Send Message
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </MotionPaper>
        </Container>

        {/* Location Section */}
        <Container maxWidth="xl">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <MotionBox
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <Typography
                  variant="h3"
                  gutterBottom
                  sx={{
                    fontFamily: '"Playfair Display", serif',
                    mb: 3,
                  }}
                >
                  Our Location
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: 'text.secondary', mb: 4 }}>
                  Located in the heart of Puri, Bisworupa Inn offers easy access to the city's most iconic attractions. Our prime location near the Light House makes us an ideal choice for both leisure and pilgrimage travelers.
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    border: '1px solid',
                    borderColor: alpha(theme.palette.primary.main, 0.1),
                    borderRadius: 2,
                    mb: 4,
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      Address
                    </Typography>
                    <Typography variant="body1">
                      PM/B-69, Baliapanda,<br />
                      Near Light House, Puri,<br />
                      Odisha, 752001, India
                    </Typography>
                  </Stack>
                </Paper>
                <Button
                  variant="contained"
                  href="https://maps.google.com/?q=Bisworupa+Inn+Puri"
                  target="_blank"
                  startIcon={<LocationIcon />}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                  }}
                >
                  Get Directions
                </Button>
              </MotionBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MotionPaper
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                elevation={3}
                sx={{
                  height: 400,
                  borderRadius: 4,
                  overflow: 'hidden',
                }}
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3747.0374146597584!2d85.8317885!3d19.8007775!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a19c4f7dd844adb%3A0x4e9f00cdf49d3cb7!2sBisworupa%20Inn!5e0!3m2!1sen!2sin!4v1647901234567!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </MotionPaper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  )
}

export default Contact 